import React from 'react';
import { useSelector } from 'react-redux';
import { getLocaleData } from '@/selectors/localeSelectors';
import { useCommonContent } from '@/hooks/oneCmsIntlHooks/oneCmsIntlHelperHooks';
import * as loungeHelper from '@/helpers/loungeHelper';
import { getOneCmsLoungeFinderImagesBaseUrl } from '@/selectors/oneCmsSelectors';
import { getLastViewedProduct } from '@/selectors/lastViewedProductSelectors';
import { Lounge } from '@/types';
import LFLink from './LFLink';

interface SearchLoungeResultNoCarouselProps {
  heading?: string;
  headingLevel?: string | number;
  description?: string;
  lounge: Lounge;
  trackElement?: string;
  trackDetail?: string;
  hideLoungeStatus?: boolean;
}

const SearchLoungeResultNoCarousel = ({
  heading,
  headingLevel,
  description,
  lounge,
  trackElement,
  trackDetail,
  hideLoungeStatus = false,
}: SearchLoungeResultNoCarouselProps) => {
  const { name, id } = lounge;

  const {
    lounges: { open: openLabel, closed: closedLabel, opensAt, closesAt },
    config: {
      dateFormatting: { timeFormat },
    },
  } = useCommonContent();
  const lastViewedProduct = useSelector(getLastViewedProduct);
  const { locale } = useSelector(getLocaleData);
  const oneCmsBaseImgUrl = useSelector(getOneCmsLoungeFinderImagesBaseUrl);
  const placeholderImgUrl = `${oneCmsBaseImgUrl}placeholder.jpg`;
  const isLoungeTempClosed = loungeHelper.isLoungeTempClosed(lounge);

  const {
    isOpen: isLoungeOpen,
    openingTime,
    closingTime,
  } = loungeHelper.getLoungeCurrentStatus(locale, lounge, timeFormat);

  const hasInvalidTimings = !openingTime || !closingTime;

  const loungeTerminal = lounge.location?.terminal;
  const loungePath = loungeHelper.generateLoungeUrl({
    locale,
    loungeId: lounge.id,
    loungeName: lounge.name,
    loungeTerminal,
  });

  const loungeImages = lounge.images?.length
    ? lounge.images
    : [{ images: { medium: placeholderImgUrl, public_id: 'placeholder' } }];

  const loungeOperatingStartStopTime = () => {
    if (isLoungeTempClosed) return null;

    return isLoungeOpen ? (
      <span className="tw-font-normal">
        &nbsp;• {closesAt} {closingTime}
      </span>
    ) : (
      <span className="tw-font-normal">
        &nbsp;• {opensAt} {openingTime}
      </span>
    );
  };

  const DEFAULT_HEADING_LEVEL = 3;
  const level = Math.min(
    6,
    Math.max(1, Number(headingLevel || DEFAULT_HEADING_LEVEL))
  );
  const HeadingTag = `h${level}` as keyof JSX.IntrinsicElements;

  return (
    <LFLink
      className="tw-mb-2 tw-w-full tw-self-stretch sm_tw-mb-0"
      aria-label={`${lounge.name} ${loungeTerminal}`}
      to={`${lastViewedProduct}/${lounge.airport?.iata}/${loungePath}/`}
      key={`lounge-result-link-${id}`}
      detail={trackDetail || `lounge-result-link-${id}`}
      element={trackElement || 'lounge-result-link'}
    >
      <div className="tw-group tw-h-full tw-rounded-2xl tw-border tw-border-gray-300">
        <div className="tw-relative">
          <img
            src={loungeImages[0]?.images?.medium}
            alt="Lounge Thumbnail"
            width={300}
            height={200}
            className="tw-aspect-video tw-w-full tw-rounded-t-2xl tw-object-cover"
          />
        </div>
        <div className="tw-px-4 tw-pb-4 tw-pt-3 tw-text-left sm_tw-px-6 sm_tw-pb-6 sm_tw-pt-4">
          <HeadingTag className="tw-text-small tw-font-bold tw-leading-body tw-text-gray-900 sm_tw-heading-md-medium">
            {heading || name}
          </HeadingTag>
          <div className="sm_tw-body-small tw-text-xs tw-text-gray-800">
            <p>{description || loungeTerminal}</p>
            {!hideLoungeStatus &&
              (!hasInvalidTimings || isLoungeTempClosed) && (
                <div id={`lounge-status-${id}`}>
                  {' '}
                  {isLoungeOpen ? (
                    <span className="tw-text-status-success">{openLabel}</span>
                  ) : (
                    <span className="tw-text-status-caution">
                      {closedLabel}
                    </span>
                  )}
                  {loungeOperatingStartStopTime()}
                </div>
              )}
          </div>
        </div>
      </div>
    </LFLink>
  );
};

export default SearchLoungeResultNoCarousel;
